<template>
  <div class="icon-customize">
    <div class="row roomy align-middle margin-bottom-xl">
      <div class="column-3 order-last">
        <div class="text-right">
          <button
            class="size-xs flat with-border text-uppercase margin-bottom-0"
            style="--button-background: transparent; --border-color: var(--fa-dk-blue); --button-color: var(--fa-dk-blue); letter-spacing: var(--spacing-6xs)"
            @click.prevent="close"
          >
            Close Wizard
          </button>
        </div>
      </div>
      <div class="column-9">
        <h2
          class="text-left margin-top-0"
          style="color: var(--fa-navy); line-height: 1.2"
        >
          <span class="sr-only">Add modifiers to</span>
          <span v-if="modType == ''">{{ icon.name }}</span>
          <span v-else>
            <span class="muted">{{ selectedFamily == 'sharp' ? 'sharp-' : '' }}{{ makeDuotone ? 'duotone-' : '' }}{{ selectedStyle }}-</span
            ><span>{{ icon.name }}</span
            ><span class="muted">-{{ modName }}</span>
          </span>
        </h2>
      </div>
    </div>
    <div class="wrap-icon-wizard row roomy align-stretch">
      <div class="icon-wizard-grid-preview column-12 laptop:column-6 laptop:margin-bottom-0">
        <div
          v-show="!loading"
          class="display-flex flex-column flex-content-center position-relative align-stretch flex-content-between"
          style="height: 100%; gap: 3rem"
        >
          <template v-if="isShowingDuotoneWarning">
            <div
              class="card roomy icon-details-preview with-border with-border-radius flex-content-center display-flex flex-column flex-items-center flex-align-center margin-bottom-0"
              style="height: 100%; --border-color: var(--fa-lt-gravy)"
            >
              <div class="icon-details-preview-rendering">
                <div
                  style="opacity: 0.5; width: var(--icon-details-preview-font-size); margin: 0 auto; font-size: 9rem"
                  class="padding-y-2xs"
                >
                  <i :class="`fa ${selectedFamily} fa-solid  fa-${icon.name}`" />
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <template v-if="isProUser">
              <div
                class="card roomy icon-details-preview with-border with-border-radius flex-content-center display-flex flex-column flex-items-center flex-align-center margin-bottom-0"
                style="height: 100%; --border-color: var(--fa-lt-gravy)"
              >
                <div class="icon-details-preview-rendering">
                  <div style="width: var(--icon-details-preview-font-size); margin: 0 auto; transform: translateX(1.5rem)">
                    <svg
                      v-if="makeDuotone === false || modType === ''"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                      style="fill: var(--fa-navy); display: block; width: 100%; height: 100%"
                    >
                      <path :d="path" />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                      style="fill: var(--fa-navy); display: block; width: 100%; height: 100%"
                    >
                      <path
                        class="fa-primary"
                        :d="path"
                      />
                      <path
                        class="fa-secondary"
                        opacity=".4"
                        :d="secondaryPath"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <icon-preview
                :display-icon-preview-controls="false"
                :styling="defaultStyles"
                :icon="icon"
                :default-styles="defaultStyles"
                :version="version"
                preview-mode="day"
              />
            </template>
          </template>
        </div>

        <div v-show="loading">
          <wa-element-loading
            class="display-flex flex-column flex-items-center flex-content-center"
            :content="loadingMessage"
            :custom-style="{
              'min-height': 'var(--element-sm-loading-min-height)'
            }"
          />
        </div>
      </div>

      <div class="icon-wizard-use display-flex flex-column column-12 laptop:column-6 margin-bottom-2xl tablet:margin-bottom-0">
        <icon-family-style
          :icon="icon"
          :styling="styling"
          :version="version"
          :default-styles="defaultStyles"
          @change="(value) => change(value)"
        />
        <div
          class="card display-flex flex-column flex-content-between margin-bottom-0"
          :style="summaryStyle"
        >
          <div
            v-if="isShowingDuotoneWarning"
            class="display-flex flex-column flex-content-center"
            style="height: 100%"
          >
            <div class="text-center">
              <h3 class="margin-y-xs">Sorry!</h3>
              <p>Icon Modifiers aren't ready for Duotone yet&hellip;</p>
            </div>
          </div>

          <div
            v-else-if="!loadingKits"
            class="display-flex flex-column flex-content-between"
            style="height: 100%"
          >
            <template v-if="isProUser">
              <p class="margin-bottom-md">
                Add a modifier to the <strong>{{ icon.name }}</strong> icon and save it directly to a Pro Kit of your choice. Use it just like any other
                <site-link
                  :to="$docs('/web/add-icons/upload-icons')"
                  style="--link-color: initial"
                  >custom uploaded icon</site-link
                >!
              </p>
              <div v-if="isReady">
                <wa-select-custom
                  label="Choose a Kit to add this icon to"
                  is-minimal
                  class="size-sm"
                  :custom-style="{
                    '--input-font-weight': 'var(--font-weight-normal)'
                  }"
                  @change="(value) => onKitChange(value)"
                >
                  <template slot="options">
                    <option value="">Select a Kit...</option>
                    <option
                      v-for="kit in kits"
                      :key="kit.token"
                      :value="kit.token"
                    >
                      {{ kit.name }}
                    </option>
                  </template>
                </wa-select-custom>

                <div class="padding-top-xs">
                  <button
                    v-if="!uploading"
                    class="button blue size-sm"
                    style="width: 100%; vertical-align: center"
                    :disabled="disableSave"
                    @click.prevent="saveIcon()"
                  >
                    <font-awesome-icon
                      :icon="$v6.solid.faSuitcase"
                      size="lg"
                      class="margin-right-3xs"
                    />
                    Save to Kit
                  </button>

                  <button
                    v-else
                    class="button blue padding-y-lg"
                    style="width: 100%; vertical-align: center"
                    disabled="true"
                  >
                    <font-awesome-icon
                      :icon="$v6.regular.faSync"
                      spin
                      size="lg"
                      class="margin-right-3xs"
                    />
                    Saving ...
                  </button>
                  <dl
                    class="display-flex flex-row flex-items-center flex-wrap margin-top-md"
                    style="gap: var(--spacing-md); --list-line-height: 1.2; --list-padding-left: 0"
                  >
                    <dt
                      class="margin-y-0 margin-right-0 size-sm"
                      style="font-weight: var(--font-weight-normal)"
                    >
                      <font-awesome-icon
                        :icon="$v6.solid.faCircleInfo"
                        fixed-width
                        size="lg"
                        style="color: var(--fa-blue)"
                      />
                      <span class="sr-only margin-left-2xs">Learn about&hellip; </span>
                    </dt>
                    <dd class="margin-bottom-0 size-sm">
                      <site-link :to="$docs('/web/add-icons/upload-icons')">Custom Icons</site-link>
                    </dd>
                    <dd class="margin-bottom-0 size-sm">
                      <site-link :to="$docs('/web/add-icons/icon-wizard')">Icon Wizard</site-link>
                    </dd>
                  </dl>
                </div>
              </div>
              <div v-else>
                <p><site-link :to="{ name: 'kits' }">Create a Pro Kit</site-link> to get started!</p>
              </div>
            </template>
            <template v-else>
              <div>
                <div class="display-flex flex-row flex-content-between margin-bottom-sm">
                  <h3 class="h5 margin-0">Icon Wizard</h3>
                  <span class="tag yellow">Pro</span>
                </div>

                <p>
                  With the
                  <site-link
                    :to="$docs('/web/add-icons/icon-wizard')"
                    style="--link-color: initial"
                    >Icon Wizard</site-link
                  >, you can add an icon modifier the <strong>{{ icon.name }}</strong> icon or any of our other icons with the click of a button. Save it
                  directly to a Pro Kit, and use it just like any other
                  <site-link
                    :to="$docs('/web/add-icons/upload-icons')"
                    style="--link-color: initial"
                    >custom uploaded icon</site-link
                  >!
                </p>
              </div>
              <div class="text-center">
                <site-link
                  :to="{ name: 'plans' }"
                  class="button block accent margin-top-md"
                  >Get a Pro Plan + Use the Icon Wizard</site-link
                >
                <p class="size-sm margin-top-lg">
                  Already have a Pro Plan?
                  <site-link
                    :to="{ name: 'sessions.sign-in', query: { next: signInNext } }"
                    class="margin-left-2xs"
                    style="--link-color: initial"
                    @click.native="$posthog.capture('sign_in_button_click', { context: 'icon_wizard_modal' })"
                  >
                    Sign in
                    <font-awesome-icon :icon="$v6.regular.faRightToBracket" />
                  </site-link>
                </p>
              </div>
            </template>
          </div>

          <wa-element-loading
            v-else
            class="display-flex flex-column flex-items-center flex-content-center"
            :content="loadingMessage"
            :custom-style="{
              height: '100%',
              color: 'var(--fa-navy)'
            }"
          />
        </div>
      </div>

      <div class="icon-wizard-modifiers display-flex position-relative column-12 margin-top-xl margin-bottom-xl laptop:margin-bottom-0">
        <div
          id="chooser"
          class="display-flex flex-row flex-wrap"
          style="gap: var(--spacing-sm)"
        >
          <div
            class="buttons"
            style="
              flex-grow: 3;
              --button-margin-bottom: 0;
              --button-padding: var(--spacing-xs);
              --button-background: var(--fa-lt-gravy);
              --button-color: var(--fa-navy);
            "
          >
            <button
              :class="['checkbox-make-duotone flat compact with-border', { active: makeDuotone }]"
              style="line-height: 1"
              :disabled="isShowingDuotoneWarning || !isProUser"
              aria-label="Make it Duotone"
              data-balloon-pos="up"
              @click="toggleDuotone()"
            >
              <font-awesome-icon
                :icon="$v6.duotone.faCircleHalf"
                size="xl"
                :transform="{ rotate: 45 }"
                fixed-width
              />
              <span
                v-if="makeDuotone"
                class="sr-only"
                >Duotone-mode on</span
              >
              <span
                v-else
                class="sr-only"
                >Duotone-mode off</span
              >
            </button>
            <button
              :class="`icon-action-modifier flat compact with-border ${modified === false ? '' : ''}`"
              style="line-height: 1"
              :disabled="isShowingDuotoneWarning || !isProUser"
              aria-label="Remove modifier"
              data-balloon-pos="up"
              @click.prevent="showOriginal()"
            >
              <font-awesome-icon
                :icon="$v6.solid.faBan"
                size="xl"
                fixed-width
              />
            </button>
          </div>
          <button
            v-for="mod in iconModifers"
            :key="mod.type"
            :class="`icon-action-modifier flat compact size-xs display-inline-block ${mod.type == modType ? 'active' : ''}`"
            style="
              --button-margin-bottom: 0;
              --button-padding: var(--spacing-xs);
              --button-hover-background: var(--fa-lt-gravy);
              --button-hover-color: var(--fa-dk-blue);
              line-height: 1;
            "
            :disabled="isShowingDuotoneWarning || !isProUser"
            @click.prevent="modifyIcon(mod.type)"
          >
            <i :class="`${modifierFamily} fa-${modifierStyle} fa-2x fa-fw ${mod.icon}`" />
          </button>
        </div>

        <site-link
          v-if="!isProUser"
          :to="{ name: 'plans' }"
          aria-label="Go Pro to Use the Icon Wizard!"
          data-balloon-pos="up"
          style="position: absolute; height: 100%; width: 100%; top: 0; left: 0"
        ></site-link>
      </div>
    </div>

    <customize-alerts
      id="alerts"
      :loading="loading || loadingKits"
      :error-message="errorMessage"
      :kits="kits"
      :selected-kit="selectedKit"
      :icon-name="iconName"
      :duplicate-error="duplicate"
      :success="success"
    />
  </div>
</template>

<script>
  import path from 'path'
  import IconFamilyStyle from '../details/IconFamilyStyle'
  import IconPreview from '../details/IconPreview'
  import sample from 'lodash/sample'
  import { parse, ModifierLayout, SvgOutputOptions, SvgModifyOptions } from '@fortawesome-internal-tools/viggo'
  import { FAEditableFamilyIds } from '@fortawesome-internal-tools/fontawesome-icons/canonical' // eslint-disable-line import/no-unresolved
  import { mapGetters, mapActions } from 'vuex'
  import CustomizeAlerts from './CustomizeAlerts'
  import { loadViggo } from 'app/viggo'
  import { EventTrackingMixin } from 'app/mixins'

  const LOADING_MESSAGES = [
    'Unloading fresh icons off of the fa-truck…',
    'Uploading fa-space-station-moon plans…',
    'Taking icons out of the fa-oven. Can you smell them?',
    'Finding the key to the icon fa-treasure-chest…'
  ]

  const modifers = require('./iconModifers.json')

  export default {
    name: 'IconCustomize',
    components: { CustomizeAlerts, IconFamilyStyle, IconPreview },

    mixins: [EventTrackingMixin],

    props: {
      defaultStyles: {
        type: Object,
        required: true
      },
      icon: {
        type: Object,
        required: true
      },
      isProUser: {
        type: Boolean,
        required: true
      },
      isUsingModal: {
        type: Boolean,
        required: true
      },
      selectedFamily: {
        type: String,
        required: true
      },
      selectedStyle: {
        type: String,
        required: true
      },
      styling: {
        type: Object,
        required: true
      },
      version: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        loadingMessage: sample(LOADING_MESSAGES),
        cutout: '',
        modType: '',
        modName: '',
        layout: '',
        fetchedSVG: '',
        loading: false,
        loadingKits: false,
        uploading: false,
        success: false,
        svg: null,
        options: null,
        errorMessage: '',
        duplicate: false,
        selectedKit: '',
        modified: false,
        makeDuotone: false,
        path: '',
        secondaryPath: '',
        originalPath: '',
        viggoLoading: false,
        viggoLoadSuccess: false,
        viggo: null
      }
    },

    computed: {
      ...mapGetters('customize', ['currentKitToken']),
      ...mapGetters('kits', ['sortedKits']),

      activeVersion() {
        return `v${this.$releases.active.version}`
      },

      iconModifers() {
        return modifers
      },

      isReady() {
        return this.kits.length > 0
      },

      iconName() {
        return this.modType == '' ? this.icon.name : this.generateIconName()
      },

      disableSave() {
        return this.loading || this.modType == '' || this.duplicate || this.success || this.selectedKit == ''
      },

      kits() {
        return this.sortedKits.filter((kit) => kit.licenseSelected == 'pro')
      },

      modifierStyle() {
        // Regular uses solid modifiers
        return this.selectedStyle == 'regular' ? 'solid' : this.selectedStyle
      },

      modifierFamily() {
        return this.selectedFamily == 'classic' ? '' : `fa-${this.selectedFamily}`
      },

      isShowingDuotoneWarning() {
        return !FAEditableFamilyIds.includes(this.selectedFamily)
      },

      selectedKitToken() {
        return this.currentKitToken
      },

      signInNext() {
        return path.join(this.$router.history.base, this.$route.fullPath)
      },

      summaryStyle() {
        let style = {}

        if (this.isProUser) {
          style['--card-background'] = 'var(--blue1)'
        }

        if (!this.isProUser) {
          style['--card-background'] = 'var(--yellow1)'
        }

        return {
          ...{ '--desktop-grid-max-width': 'var(--laptop-grid-max-width)' },
          ...{ '--card-text-align': 'left' },
          ...{ '--link-hover-color': 'var(--fa-navy)' },
          ...{ '--card-vertical-padding': 'var(--spacing-xl)' },
          ...{ '--card-horizontal-padding': 'var(--spacing-xl)' },
          ...{ 'flex-grow': '1' },
          ...style
        }
      }
    },

    watch: {
      async icon(val) {
        this.clear()
        await this.load()
      },

      async selectedStyle(val) {
        this.clear()
        this.modType = ''
        await this.load()
      },

      async selectedFamily(val) {
        this.clear()
        this.modType = ''
        await this.load()
      }
    },

    async beforeMount() {
      try {
        this.viggoLoading = true
        this.viggo = await loadViggo()
        this.viggoLoadSuccess = true
      } catch (e) {
        this.viggoLoadSuccess = false
      } finally {
        this.viggoLoading = false
      }
    },

    async created() {
      await this.load()

      if (this.$route.meta.isSignedIn) {
        this.loadingKits = true

        this.fetchKits().then((r) => {
          if (this.currentKitToken) {
            this.selectedKit = this.currentKitToken
          }

          this.loadingKits = false
        })
      }
    },

    methods: {
      ...mapActions('customize', ['setKitToken']),
      ...mapActions('kits', ['fetchKits']),
      ...mapActions('iconUploads', ['createIconUpload']),
      ...mapActions('kitPackages', ['uploadPackage']),

      async fetchSVG() {
        const response = await fetch(this.getResourceUrl())

        if (response.status < 400) {
          this.fetchedSVG = await response.text()
          this.originalPath = this.getPathFromSVG(this.fetchedSVG)
          this.path = this.originalPath
        }
      },

      async load() {
        if (!this.loading) {
          this.loading = true
          this.clear()
          await this.fetchSVG()
          this.loading = false
        }
      },

      getPathFromSVG(svg) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(svg, 'image/svg+xml')
        return doc.documentElement.firstElementChild.getAttribute('d')
      },

      checkForDuplicateIconName() {
        this.fetchKits().then((response) => {
          if (this.selectedKit) {
            const kit = this.kits.find((k) => k.token === this.selectedKit)
            const iconName = this.generateIconName()
            const matchingIcons = kit.iconUploads.find((icon) => icon.name === iconName)

            this.duplicate = matchingIcons != undefined ? true : false
          }
        })
      },

      getResourceUrl() {
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/${this.activeVersion}/svgs/${
          this.selectedFamily == 'classic' ? '' : `${this.selectedFamily}-`
        }${this.selectedStyle}/${this.icon.name}.svg`
      },

      getModifierLayout(layout) {
        const mod_layouts = {
          FullWidthCenter: ModifierLayout.FullWidthCenter,
          BottomRight: ModifierLayout.BottomRight
        }

        return mod_layouts[layout]
      },

      async modifyIcon(modType) {
        this.clear()

        if (FAEditableFamilyIds.includes(this.selectedFamily)) {
          if (this.isProUser) {
            if (this.modType == modType) {
              this.showOriginal()
            } else {
              this.modified = true
              this.modType = modType
              await this.fetchSVG()
              const modConfig = this.loadModifiers(modType)

              this.modName = modConfig.name
              this.renderIcon(modConfig.cutout, modConfig.modifier, modConfig.layout)
              this.checkForDuplicateIconName()

              this.$posthog.capture('icon_wizard_modifier_button_click', {
                modifier: this.modName,
                modifier_style: this.modifierStyle
              })
            }
          }
        }
      },

      onKitChange(value) {
        this.setKitToken(value)
        this.selectedKit = this.currentKitToken
        this.checkForDuplicateIconName()
      },

      loadModifiers(modType) {
        const mod = modifers.filter((obj) => {
          return obj.type == modType
        })[0]

        var result = mod.styles.filter((obj) => {
          return obj.style === this.selectedStyle && obj.family === this.selectedFamily
        })[0]

        const cutout = result.cutout ? result.cutout : mod.defaultCutout

        return {
          name: mod.name,
          cutout: cutout,
          modifier: result.modifier,
          layout: this.getModifierLayout(mod.layout)
        }
      },

      renderIcon(cutout, modifier, layout) {
        const options = new SvgModifyOptions({
          layout: layout,
          duotone: this.makeDuotone
        })

        this.svg = parse(this.fetchedSVG)
        this.svg.addModifier(cutout, modifier, options)
        this.options = new SvgOutputOptions({ roundToDecimalPlaces: 1 })
        this.svg.optimize(this.options)
        this.path = this.svg.primaryPathString(this.options)

        if (this.makeDuotone) {
          this.secondaryPath = this.svg.secondaryPathString(this.options)
        }
      },

      saveIcon() {
        if (this.modified) {
          const name = this.generateIconName()
          this.$posthog.capture('icon_wizard_save_button_click')
          const selectedKitToken = this.selectedKit
          const kit = this.kits.find((k) => k.token === selectedKitToken)
          if (!kit) {
            this.errorMessage = 'Something went wrong. Try reloading this page and trying again.'
            return
          }
          this.uploadFile(selectedKitToken, name)
            .then(() => {
              if (kit.kitPackageEnabled) {
                // republish the npm package
                return this.uploadPackage(kit.token)
              }
            })
            .catch(console.error)
        } else {
          this.errorMessage = 'You must modify the icon before saving it.'
        }
      },

      generateIconName() {
        const iconName = `${this.makeDuotone ? 'duotone-' : ''}${this.selectedStyle}-${this.icon.name}-${this.modName}`.toLowerCase()
        return this.selectedFamily == 'classic' ? iconName : `${this.selectedFamily}-${iconName}`
      },

      async uploadFile(kitToken, name) {
        try {
          this.uploading = true

          await this.createIconUpload({
            kitToken,
            name,
            width: this.svg.width().toString(),
            height: this.svg.height().toString(),
            path: this.svg.primaryPathString(this.options),
            pathSecondary: this.svg.secondaryPathString(this.options)
          })

          this.uploading = false
          this.success = true
          this.$posthog.capture('icon_wizard_upload_submit', {
            icon_name: this.icon.name,
            modifier: this.modName,
            modifier_style: this.modifierStyle
          })
        } catch (error) {
          this.$posthog.capture('icon_wizard_upload_fail')
          this.errorMessage = 'Error uploading file. Please try again later.'
          this.uploading = false
          this.success = false
        }
      },

      showOriginal() {
        this.clear()
        this.modType = ''
        this.path = this.originalPath
      },

      close() {
        this.clear()
        this.$posthog.capture('icon_wizard_cancel')
        this.$emit('closeEditor')
      },

      clear() {
        this.errorMessage = ''
        this.uploading = false
        this.success = false
        this.duplicate = false
        this.modified = false
      },

      change(value) {
        return this.$emit('change', value)
      },

      toggleDuotone() {
        this.makeDuotone = !this.makeDuotone
        this.modifyIcon(this.modType)
      }
    }
  }
</script>
